import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router'
import { StoresContext } from '@App/contexts'
import Navigation from '@App/navigation'
import Button from '@Components/Buttons/Button'
import ModalPortal from '@Components/Popups/ModalPortal'
import WorkMergeVoteConfirmationPopup from '@Components/Popups/ConfirmationPopup/WorkMergeVoteConfirmationPopup'
import { WorkMergeRequestStatus, WorkMergeRequestWork, WorkMergeVote } from '@Entities/rightsholder/work-merge'
import WorkMergeReason from '@Scenes/RightsholderScene/WorkMerge/WorkMergeReason'
import WorkMergeRequestPage from '@Scenes/RightsholderScene/WorkMerge/WorkMergeRequestPage'
import WorkMergeReviewVotes from '@Scenes/RightsholderScene/WorkMerge/WorkMergeReviewRequest/WorkMergeReviewVotes'
import { workMergeStatusText } from '@Scenes/RightsholderScene/WorkMerge/WorkMergeStatusPill'
import WorkMergeCandidate from '@Scenes/RightsholderScene/WorkMerge/WorkMergeSearch/components/WorkMergePanel/WorkMergePrimarySelection/WorkMergeCandidate'
import './index.scss'

const closedStatuses: WorkMergeRequestStatus[] = ['ACCEPTED', 'COMPLETED']

const WorkMergeReviewRequest = () => {
    const { workMergeReviewDetailStore, rightsholderStore } = useContext(StoresContext)
    const navigate = useNavigate()
    const [savedVote, setSavedVote] = useState<WorkMergeVote>()

    const goToMyReviews = () =>
        navigate(Navigation.rightholder.workMerge.forMyReview(rightsholderStore.currentRightsholderId))

    const submitDecision = async (vote: WorkMergeVote) => {
        if (await workMergeReviewDetailStore.voteOnRequest(vote)) {
            setSavedVote(vote)
        }
    }

    const renderWork = (requestWork: WorkMergeRequestWork) => (
        <div className="work-merge-review-request-work" key={requestWork.workId}>
            {requestWork.work ? (
                <WorkMergeCandidate
                    work={requestWork.work}
                    yourShares={rightsholderStore.findSpecificWorkClaimedShares(requestWork.work.id)}
                />
            ) : (
                <p className="work-merge-request-page-empty">
                    {requestWork.title} ({requestWork.songCode})
                </p>
            )}
            <WorkMergeReviewVotes votes={requestWork.votes ?? []} />
        </div>
    )

    return (
        <WorkMergeRequestPage store={workMergeReviewDetailStore} className="work-merge-review-request">
            {(request) => {
                const works = [request.primaryWork, ...request.secondaryWorks]

                return (
                    <>
                        <ModalPortal isOpen={!!savedVote}>
                            {savedVote && <WorkMergeVoteConfirmationPopup vote={savedVote} onClose={goToMyReviews} />}
                        </ModalPortal>

                        <h6 className="work-merge-review-request-section">Review Work Merge Request</h6>

                        <div className="work-merge-review-request-primary">
                            <div className="work-merge-review-request-primary-heading">
                                <p className="work-merge-review-request-primary-title">Primary Work</p>
                                <p className="work-merge-review-request-primary-status">
                                    Request {workMergeStatusText[request.status]}
                                </p>
                            </div>
                            <p>Work Title: {request.primaryWork.title}</p>
                            <p>
                                Song Code: <strong>{request.primaryWork.songCode}</strong>
                            </p>
                            {request.primaryWork.memberWorkIdentifier && (
                                <p>
                                    Member Work Identifier: <strong>{request.primaryWork.memberWorkIdentifier}</strong>
                                </p>
                            )}
                        </div>

                        <p className="work-merge-review-request-works-label">
                            Works to be Merged into Primary ({works.length})
                        </p>

                        <div className="work-merge-review-request-works">{works.map(renderWork)}</div>

                        <WorkMergeReason reason={request.reason} />

                        <div className="work-merge-request-page-footer">
                            <Button type="secondary" mainColor onClick={goToMyReviews}>
                                Back
                            </Button>
                            {!closedStatuses.includes(request.status) && (
                                <div className="work-merge-review-request-decisions">
                                    <Button
                                        type="secondary"
                                        mainColor
                                        disabled={workMergeReviewDetailStore.isSavingVote}
                                        onClick={() => submitDecision('REJECTED')}
                                    >
                                        Reject Merge request
                                    </Button>
                                    <Button
                                        type="primary"
                                        mainColor
                                        disabled={workMergeReviewDetailStore.isSavingVote}
                                        onClick={() => submitDecision('ACCEPTED')}
                                    >
                                        Accept Merge Request
                                    </Button>
                                </div>
                            )}
                        </div>
                    </>
                )
            }}
        </WorkMergeRequestPage>
    )
}

export default observer(WorkMergeReviewRequest)
